import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { isWeatherForecast, type WeatherWarning } from '@casa/driver-sdk';
import { drawText } from '../../elements/flex_label.ts';
import type { IconDefinition } from '../../elements/icon.ts';
import { lastValue } from '../../values/readings.ts';
import { CanvasView } from '../../view/canvas_view.tsx';
import { findLegacyIcon } from '../../view/icons.ts';
import { CARD_COLORS, CardButton, PickerCard } from '../../view/picker_card.tsx';
import { TILE_FONT } from '../../view/theme.ts';
import { useTileTextColor } from '../../view/tile_style.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { DIM_COLOR, WARNING_COLOR, drawForecast, forecastContent, forecastLayout, iconNamesOf, spanEdge, type ForecastLayout, type ForecastTile } from './forecast_tile.ts';

const MINUTE = 60_000;

/** The time, moved on each minute: "now" for which hours and slots are past. */
function useMinute() : number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), MINUTE);
        return () => clearInterval(timer);
    }, []);

    return now;
}

/** The icons by name, loaded as they are asked for; the version changes when one arrives. */
function useIcons(names: readonly string[]) : { lookup: (name: string) => IconDefinition | undefined; version: number } {
    const loaded = useRef(new Map<string, IconDefinition | undefined>());
    const [version, setVersion] = useState(0);
    const key = names.join(',');

    useEffect(() => {
        let isCurrent = true;
        const missing = key.split(',').filter((name) => name !== '' && !loaded.current.has(name));

        void Promise.all(missing.map(async (name) => [name, await findLegacyIcon(name)] as const)).then((found) => {
            if (!isCurrent || found.length === 0) {
                return;
            }
            for (const [name, icon] of found) {
                loaded.current.set(name, icon);
            }
            setVersion((count) => count + 1);
        });
        return () => {
            isCurrent = false;
        };
    }, [key]);

    return { lookup: (name) => loaded.current.get(name), version };
}

/** The warnings in full (WT-5): each with its title, sender, time span and text. */
function WarningsCard({ anchor, title, warnings, now, timeZone, onClose }: { anchor: DOMRect; title: string; warnings: readonly WeatherWarning[]; now: number; timeZone: string; onClose: () => void }) : ReactNode {
    const closeButton = useRef<HTMLButtonElement>(null);

    return (
        <PickerCard anchor={anchor} title={title} aside={warnings.length === 1 ? '1 warning' : `${warnings.length} warnings`} label="warnings" initialFocus={closeButton} onClose={onClose}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '14px 0', userSelect: 'text', WebkitUserSelect: 'text' }}>
                {warnings.map((warning) => (
                    <div key={warning.id} style={{ background: CARD_COLORS.raised, borderRadius: 10, padding: '10px 12px', borderLeft: `4px solid ${WARNING_COLOR}` }}>
                        <div style={{ fontWeight: 600, fontSize: 16 }}>{warning.title}</div>
                        <div style={{ fontSize: 13, color: CARD_COLORS.muted, margin: '2px 0 6px' }}>
                            {spanEdge(warning.start, now, timeZone)} – {spanEdge(warning.end, now, timeZone)}
                            {warning.sender ? ` · ${warning.sender}` : ''}
                        </div>
                        <div style={{ fontSize: 14, lineHeight: 1.35 }}>{warning.description}</div>
                    </div>
                ))}
            </div>
            <div style={{ display: 'flex' }}>
                <CardButton label="Close" isMain buttonRef={closeButton} onPress={onClose} />
            </div>
        </PickerCard>
    );
}

/** The content area of a `forecast` tile (docs/specs/weather.md §4). */
export function ForecastTileView({ tile }: { tile: ForecastTile }) : ReactNode {
    const environment = useTileEnvironment();
    const value = lastValue(environment.useReadings(tile, tile.options.source));
    const forecast = isWeatherForecast(value) ? value : undefined;
    const now = useMinute();
    const content = forecast ? forecastContent(tile, forecast, now, environment.locale) : undefined;
    const icons = useIcons(content ? iconNamesOf(content) : []);
    const color = useTileTextColor('#ffffff');
    const layout = useRef<ForecastLayout | undefined>(undefined);
    const area = useRef<HTMLDivElement>(null);
    const [anchor, setAnchor] = useState<DOMRect | null>(null);

    // A tap on the strip opens the warnings (WT-5).
    const tap = (event: MouseEvent) : void => {
        const strip = layout.current?.strip;
        const box = area.current?.getBoundingClientRect();
        if (!strip || !box || !content || content.warnings.length === 0) {
            return;
        }
        const y = event.clientY - box.top;
        if (y >= strip.top && y <= strip.top + strip.height + 4) {
            // The strip's own tap, not the tile's (tiles spec TA-3).
            event.stopPropagation();
            setAnchor(area.current?.closest('[data-tile]')?.getBoundingClientRect() ?? box);
        }
    };

    return (
        <div ref={area} style={{ position: 'absolute', inset: 0 }} onClick={tap}>
            <CanvasView
                deps={[JSON.stringify(content ?? null), icons.version, color]}
                draw={(ctx, width, height) => {
                    if (!content) {
                        drawText(ctx, 'No forecast yet', { left: width * 0.1, top: height * 0.42, width: width * 0.8, height: Math.min(20, height * 0.16) }, DIM_COLOR, { ...TILE_FONT, align: 'center', valign: 'center' });
                        return;
                    }
                    const placed = forecastLayout(content, width, height);
                    layout.current = placed;
                    drawForecast(ctx, content, placed, icons.lookup, TILE_FONT, color, now);
                }}
            />
            {anchor && content && <WarningsCard anchor={anchor} title={tile.label ?? 'Weather'} warnings={content.warnings} now={now} timeZone={content.timeZone} onClose={() => setAnchor(null)} />}
        </div>
    );
}
